<template>
  <div class="chat-log-container d-flex flex-column">
    <div class="chat-log-list-wrapper">
      <div v-if="loadingChatLog" class="h-50 d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="grey"
          size="36"
          width="2"
        />
      </div>
      <div v-else-if="!!chatLog?.length" class="chat-log-list" @copy.stop>
        <div v-for="log in chatLog" :key="log.id" class="prompt" :class="{'user-prompt': !!log.userId}">
          <div v-if="!!log.userId" class="prompt-box user-prompt-box">
            <span class="chat-log-content">
              {{ log.content }}
            </span>
          </div>
          <div v-else class="prompt-box ai-response-box">
            <Markdown :source="log.content" class="markdown-content" />
          </div>
        </div>
        <dots-loader v-if="loadingResponse" />
      </div>
      <div v-else class="prompt-box ai-response-box">
        <div class="markdown-content">
          Welcome to the AI Copilot. Type instructions or questions to explore and adjust your workflow.
        </div>
      </div>
      <div ref="scrollTargetRef" />
    </div>
    <div>
      <v-form ref="chatLogForm" @submit.prevent="updateWorkflowWithAssistant">
        <div class="chat-log-form">
          <v-textarea
            :rules="[rules.required]"
            auto-grow
            rows="3"
            class="no-border"
            variant="outlined"
            v-model="aiDescription"
            placeholder="Prompt"
            @keydown.enter="onKeyDown"
          />
          <div class="d-flex justify-end">
            <v-btn type="submit" size="small" variant="outlined" :loading="loadingRequest" :disabled="loadingResponse">
              Submit
            </v-btn>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Markdown from 'vue-markdown-render'
import DotsLoader from '@/components/DotsLoader.vue'
import chatLogApi from '@/api/chatLogApi'
import safeStructuredClone from '@/utils/clone'
import { generateWorkflowAssistant } from '@/ai'
import { callFunction } from '@/api/chatGPT/chatLog/functions'
import { chatLogType } from '@/ai/prompts/common/messagesHistory'
import { getUpdatesSummaryText, getUpdateSummary } from '@/api/chatGPT/chatLog/helpers/updateSummary'

const props = defineProps({ workflow: Object, currentUIState: Object })

const scrollTargetRef = ref(null)
const aiDescription = ref('')
const loadingResponse = ref(false)
const loadingRequest = ref(false)
const chatLogForm = ref(null)
const loadingChatLog = ref(false)
const rules = ref({ required: (value) => !!value || 'Required' })

const store = useStore()
const showSidePanel = computed(() => store.state.showSidePanel)
const user = computed(() => store.state.user.user)
const chatLog = computed(() => ([...store.state.chatLog.chatLog ?? []]).reverse())
const requirementTypes = computed(() => store.state.requirementTypes.requirementTypes)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])

const updateWorkflowWithAssistant = async () => {
  try {
    const validationResult = await chatLogForm.value.validate()
    if (!validationResult?.valid) {
      return
    }

    loadingRequest.value = true
    loadingResponse.value = true
    const prompt = aiDescription.value
    resetForm()

    const messagesHistory = safeStructuredClone(chatLog.value)

    await chatLogApi.createChatLog({
      projectId: props.workflow.projectId,
      input: {
        workflowId: props.workflow.id,
        type: chatLogType.USER_PROMPT,
        content: prompt,
        userId: user.value.id
      }
    })

    loadingRequest.value = false

    const response = await generateWorkflowAssistant({
      workflow: { ...props.workflow, cardTypes: requirementTypes.value },
      messagesHistory,
      prompt,
      currentUIState: props.currentUIState
    })

    if (response?.error || !response?.output?.length) {
      return
    }

    const updatesSummary = []

    for (const output of response.output) {
      if (output.type === 'message') {
        const outputText = output.content.find((item) => item.type === 'output_text')?.text
        await chatLogApi.createChatLog({
          projectId: props.workflow.projectId,
          input: {
            workflowId: props.workflow.id,
            type: chatLogType.AI_TEXT_RESPONSE,
            content: outputText,
            aiOutput: JSON.stringify(response.output)
          }
        })
      }

      if (output.type === 'function_call') {
        const functionName = output.name
        const args = JSON.parse(output.arguments)
        const additionalArguments = {
          workflow: props.workflow,
          svgGraph: safeStructuredClone(svgGraph.value),
          svgLanes: safeStructuredClone(svgLanes.value),
          svgGroups: safeStructuredClone(svgGroups.value),
          cardTypes: safeStructuredClone(requirementTypes.value)
        }

        await callFunction(functionName, args, additionalArguments)

        const summary = getUpdateSummary(functionName, args, additionalArguments)

        if (summary) {
          updatesSummary.push(summary)
        }
      }
    }

    if (response.output.some((item) => item.type === 'function_call')) {
      await chatLogApi.createChatLog({
        projectId: props.workflow.projectId,
        input: {
          workflowId: props.workflow.id,
          type: chatLogType.AI_FUNCTION_CALL,
          content: getUpdatesSummaryText(updatesSummary),
          aiOutput: JSON.stringify(response.output)
        }
      })
    }
  } catch (e) {
    console.log(e, 'error')
  } finally {
    loadingResponse.value = false
  }
}

const onKeyDown = (event) => {
  if (event.shiftKey) {
    return
  }

  event.preventDefault()
  updateWorkflowWithAssistant()
}

const getChatLog = async (workflowId) => {
  loadingChatLog.value = true
  await chatLogApi.getChatLog({ workflowId })
  loadingChatLog.value = false
}

const resetForm = () => {
  aiDescription.value = ''
  nextTick(() => chatLogForm.value.resetValidation())
}

watch(() => props.workflow.id, () => {
  if (props.workflow.id) {
    getChatLog(props.workflow.id)
  }
}, { immediate: true })

watch([() => chatLog.value?.length, () => showSidePanel.value], async ([_, showPanel]) => {
  if (scrollTargetRef.value && showPanel) {
    await nextTick()
    scrollTargetRef.value.scrollIntoView({ behavior: 'smooth' })
  }
})
</script>

<style scoped>
.chat-log-container {
  height: calc(100vh - 114px);
}

.chat-log-list-wrapper {
  flex-grow: 1;
  margin-bottom: 20px;
  padding-right: 12px;
  overflow-y: auto;
}

.chat-log-list {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-bottom: 16px;
}

.prompt {
  display: flex;
}

.user-prompt {
  justify-content: flex-end;
}

.prompt-box {
  width: fit-content;
  max-width: 90%;
  padding: 4px;
  transition: all 0.15s;

  &.user-prompt-box {
    margin-right: 2px;
    padding: 12px 16px;
    border-radius: 16px 16px 4px 16px;
    background: #eeeeee;
    box-shadow: 3px 3px 5px 0 rgba(0, 0, 0, 0.1);
  }

  &.ai-response-box {
    margin-left: 2px;
    border-radius: 16px;

    &:first-child {
      margin-top: 2px;
    }

    &:hover {
      box-shadow: 0 0 3px 1px rgba(0, 0, 0, 0.1)
    }
  }
}

.chat-log-content {
  white-space: pre-line;
}

.chat-log-form {
  padding: 8px;
  border: 1px solid #dddddd;
  border-radius: 4px;

  & .v-btn.v-btn--size-small.v-btn--variant-outlined {
    padding: 5px 12px !important;
  }
}

:deep(.v-textarea) {
  & .v-field {
    & .v-field__outline__start,
    & .v-field__outline__end {
      border: none
    }

    & .v-field__input {
      padding: 4px;
      mask-image: none;
    }

  }

  & .v-input__details {
    display: none;
  }
}

:deep(.markdown-content) {
  margin: 16px 0;
  line-height: 1.6;

  ul {
    list-style-type: disc;
    margin: 8px 0;
    padding-left: 20px;
  }

  ol {
    list-style-type: decimal;
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  h1, h2, h3, h4 {
    margin: 16px 0 8px;
    font-weight: 600;
  }
}
</style>
